import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { LuFilePen, LuShare2 } from "react-icons/lu";
import ShareCardModal from "./ShareCardModal";

const NameRow = ({
  name,
  isMobile,
  expanded,
  handleExpand,
  getGenderLabel,
  liked,
  handleLike,
  setIsEditModalOpen,
  setSelectedName,
  syriacFontType,
}) => {
  const { isAuthenticated } = useAuth();
  const [isMeaningExpanded, setIsMeaningExpanded] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const meaningText = name.meaning || "";
  const hasLongMeaning = meaningText.length > 140;

  return (
    <React.Fragment key={name._id}>
      <tr
        className={expanded === name._id ? "expanded-row" : ""}
        onClick={() => handleExpand(name._id)}
        style={{ cursor: "pointer" }}
      >
        <td>{name.eng_word}</td>
        <td>
          <span
            className={`gender-badge ${getGenderLabel(
              name.gender,
            )?.toLowerCase()}`}
          >
            {getGenderLabel(name.gender)}
          </span>
        </td>
        {!isMobile && (
          <td>
            {Array.isArray(name.category)
              ? name.category.map((c) => (
                  <span key={c} className={`cat-badge ${c.toLowerCase()}`}>
                    {c.charAt(0).toUpperCase() + c.slice(1)}
                  </span>
                ))
              : null}
          </td>
        )}
        <td>
          <div
            style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}
          >
            {isAuthenticated && (
              <button
                className="btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedName(name);
                  setIsEditModalOpen(true);
                }}
                aria-label="Edit name"
              >
                <LuFilePen size={20} color="#7c3aed" />
              </button>
            )}
            <button
              className="btn"
              onClick={(e) => {
                e.stopPropagation();
                setIsShareModalOpen(true);
              }}
              aria-label="Share name card"
              title="Share name card"
            >
              <LuShare2 size={20} color="#7c3aed" />
            </button>
            <button
              className="btn like-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleLike(name._id);
              }}
              aria-label={liked.includes(name._id) ? "Unlike" : "Like"}
              title={liked.includes(name._id) ? "Remove from favorites" : "Add to favorites"}
            >
              <svg
                className="icon"
                viewBox="0 0 24 24"
                fill={liked.includes(name._id) ? "#e11d48" : "#bbb"}
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
              </svg>
            </button>
          </div>
        </td>
      </tr>
      {expanded === name._id && (
        <tr className="details-row">
          <td colSpan={isMobile ? 3 : 4}>
            <div className="details-box">
              {name.arb_word && (
                <div className="details-field">
                  <span className="details-label">Arabic</span>
                  <div dir="rtl">{name.arb_word}</div>
                </div>
              )}
              {name.syr_word && (
                <div className="details-field">
                  <span className="details-label">Syriac</span>
                  <div
                    className={
                      syriacFontType === "W"
                        ? "syriac-field syriac-font-western"
                        : "syriac-field syriac-font-eastern"
                    }
                    style={{ fontSize: "1.2rem" }}
                  >
                    {name.syr_word}
                  </div>
                </div>
              )}
              <div className="details-field" style={{ maxWidth: 220 }}>
                <span className="details-label">Meaning</span>
                <div
                  style={{
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                    lineHeight: 1.5,
                    maxHeight: isMeaningExpanded ? "none" : "4.5em",
                    overflow: "hidden",
                  }}
                >
                  {meaningText}
                </div>
                {hasLongMeaning && (
                  <button
                    className="btn-text"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsMeaningExpanded((prev) => !prev);
                    }}
                    aria-label={
                      isMeaningExpanded
                        ? "Collapse meaning text"
                        : "Expand meaning text"
                    }
                  >
                    {isMeaningExpanded ? "Show less" : "Show more"}
                  </button>
                )}
              </div>
              {name.eng_desc && (
                <div className="details-field">
                  <span className="details-label">Description</span>
                  <div>{name.eng_desc}</div>
                </div>
              )}
              {name.arb_desc && (
                <div className="details-field">
                  <span className="details-label">Arabic description</span>
                  <div dir="rtl">{name.arb_desc}</div>
                </div>
              )}
              {name.variations && name.variations !== "null" && (
                <div className="details-field">
                  <span className="details-label">Variations</span>
                  <div>{name.variations?.join(", ")}</div>
                </div>
              )}
              {name.src && name.src !== "null" && (
                <div className="details-field">
                  <span className="details-label">Source</span>
                  <div>{name.src}</div>
                </div>
              )}
              <div className="details-field share-name-field">
                <button
                  className="btn-text share-name-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsShareModalOpen(true);
                  }}
                >
                  <LuShare2 size={18} />
                  Share Name Card
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}
      {isShareModalOpen && (
        <ShareCardModal
          name={name}
          getGenderLabel={getGenderLabel}
          syriacFontType={syriacFontType}
          onClose={() => setIsShareModalOpen(false)}
        />
      )}
    </React.Fragment>
  );
};

export default NameRow;
